import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

// Extensions of dayjs
dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

// ?  Here you can add custom functions

function lastFridayOfTheMonthForMirvac(customHour) {
	const now = dayjs();

	// Helper: get the last Friday (weekday 5) for the month of `base`
	const getLastFriday = (base) => {
		const end = base.endOf('month'); // last day of month
		const dow = end.weekday(); // 0..6 (needs weekday plugin)
		const daysBack = (dow - 5 + 7) % 7; // distance back to Friday (5)
		return end.subtract(daysBack, 'day'); // that month's last Friday
	};

	// 1) Last Friday for this month
	let lastFriday = getLastFriday(now);

	// 2) If today is the last Friday (or after it), use next month's last Friday
	if (now.isSameOrAfter(lastFriday, 'day')) {
		lastFriday = getLastFriday(now.add(1, 'month'));
	}

	// 3) Set time to 7PM or provided custom hour
	const hourNum = Number(customHour ?? 19);
	lastFriday = lastFriday.set('hour', hourNum).set('minute', 0).set('second', 0).set('millisecond', 0);

	return lastFriday;
}

function twoMonths() {
	const date = dayjs().add(2, 'month');
	return date;
}

function threeMonths() {
	const date = dayjs().add(3, 'month');
	return date;
}

function fourMonths() {
	const date = dayjs().add(4, 'month');
	return date;
}

function maxBookingDateForBHP() {
	const date = dayjs('2025-09-30');
	return date;
}

function today() {
	const date = dayjs();
	return date;
}

function twelveMonthsFromToday() {
	return dayjs().add(12, 'month').set('hour', 23).set('minute', 59).set('second', 59).set('millisecond', 0);
}

/**
 * Calculates the start date for cleaning for Mirvac.
 * Cleaning will start on the last Friday of the month after 7PM.
 *
 * @returns {Date} The start date for cleaning.
 */
function cleaningStartDateForMirvac() {
	// for Mirvac, Cleaning will start on the last Friday of the month after 7PM
	return lastFridayOfTheMonthForMirvac();
}

/**
 * Calculates the end date for cleaning for Mirvac.
 * Cleaning will end on the last Friday of the month after 11PM.
 *
 * @returns {Date} The calculated end date for cleaning.
 */
function cleaningEndDateForMirvac() {
	// for Mirvac, Cleaning will end on the last Friday of the month after 11PM
	return lastFridayOfTheMonthForMirvac(23);
}

/**
 * Calculates a date based on the provided rule object.
 *
 * @param {Object} rule
 * @param {'today'|'static'|'addMonths'|'add'|'daily'|'lastWeekdayOfMonth'|'weekly'} rule.type
 * @param {string} [rule.dateString]      – for 'static'
 * @param {number} [rule.months]          – for 'addMonths'
 * @param {number} [rule.year]            – for 'add'
 * @param {number} [rule.month]           – for 'add'
 * @param {number} [rule.week]            – for 'add'
 * @param {number} [rule.day]             – for 'add'
 * @param {number} [rule.hour]            – for 'daily', 'weekly', or override
 * @param {number} [rule.minute]          – for 'daily', 'weekly', or override (default: 0)
 * @param {number} [rule.second]          – for 'daily', 'weekly', or override (default: 0)
 * @param {number} [rule.weekday]         – 0=Sun…6=Sat for 'lastWeekdayOfMonth' or 'weekly'
 * @param {boolean} [rule.getNextAvailable=true]
 * @returns {moment.Moment}
 */
function calculateDate(rule = {}) {
	const now = dayjs();
	const next = rule.getNextAvailable !== false;
	let date;

	switch (rule.type) {
		case 'today':
			date = now;
			break;

		case 'static':
			if (!rule.dateString) throw new Error('static rule needs dateString');
			date = dayjs(rule.dateString);
			break;

		case 'addMonths':
			date = now.add(rule.months || 0, 'month');
			break;

		case 'add': {
			let newDate = now;

			if (rule.year) newDate = newDate.add(rule.year, 'year');
			if (rule.month) newDate = newDate.add(rule.month, 'month');
			if (rule.week) newDate = newDate.add(rule.week, 'week');
			if (rule.day) newDate = newDate.add(rule.day, 'day');
			if (rule.hour) newDate = newDate.add(rule.hour, 'hour');
			if (rule.minute) newDate = newDate.add(rule.minute, 'minute');
			if (rule.second) newDate = newDate.add(rule.second, 'second');

			date = newDate;
			break;
		}

		case 'daily': {
			if (rule.hour == null) throw new Error('daily rule needs hour');
			const hourNum = Number(rule.hour);
			const minNum = Number(rule.minute || 0);
			const secNum = Number(rule.second || 0);

			date = now.set('hour', hourNum).set('minute', minNum).set('second', secNum);

			if (next && date.isBefore(now)) {
				date = date.add(1, 'day');
			}
			break;
		}

		case 'lastWeekdayOfMonth': {
			if (rule.weekday == null) throw new Error('lastWeekdayOfMonth needs weekday');
			const target = Number(rule.weekday); // 0=Sun … 6=Sat

			const findLastInMonth = (m) => {
				const end = m.endOf('month');
				const dow = end.day(); // sin plugin
				const daysBack = dow >= target ? dow - target : dow + (7 - target);
				return end.subtract(daysBack, 'day');
			};

			let candidate = findLastInMonth(now);
			if (next && now.isAfter(candidate)) {
				candidate = findLastInMonth(now.add(1, 'month'));
			}
			date = candidate;
			break;
		}

		case 'weekly': {
			if (rule.weekday == null) throw new Error('weekly needs a weekday');
			const target = Number(rule.weekday); // 0=Sun … 6=Sat
			const todayDow = now.day();
			let offset = (target - todayDow + 7) % 7;
			if (next && offset === 0) offset = 7;
			date = now.add(offset, 'day');
			break;
		}

		default:
			throw new Error(`Unknown rule type: ${rule.type}`);
	}

	// override de hora global (si viene), usando la API correcta y reasignando
	if (rule.hour != null) {
		const hourNum = Number(rule.hour);
		const minNum = Number(rule.minute || 0);
		const secNum = Number(rule.second || 0);

		date = date.set('hour', hourNum).set('minute', minNum).set('second', secNum);
	}

	return date;
}

/**
 * Apply a booking‐rule’s time onto a selected date for either the start or end of booking.
 *
 * @param {Date|moment.Moment|string} dateInput
 *   The user‐selected date (any moment-parsable form).
 * @param {Object} rule
 *   Your bookingConfig.start or bookingConfig.end rule:
 *   { type, weekday, hour, minute, second, getNextAvailable }
 * @param {boolean} [isEnd=false]
 *   If false, we’re computing the **start** time; if true, the **end** time.
 * @returns {moment.Moment}
 */
function applyRuleTime(dateInput, rule, isEnd = false) {
	const now = dayjs();
	const mSelDay = dayjs(dateInput).startOf('day');

	const minAllowed = calculateDate({ ...rule, getNextAvailable: false }).startOf('day');
	const maxAllowed = calculateDate({ ...rule, getNextAvailable: true }).startOf('day');

	const toNum = (v, d = 0) => (v == null ? d : Number(v));

	let out = mSelDay.millisecond(0);

	if (isEnd) {
		// END: if selected day is before the last allowed day, snap to 23:59:59
		if (mSelDay.isBefore(maxAllowed, 'day')) {
			out = out.set('hour', 23).set('minute', 59).set('second', 59).set('millisecond', 0);
		} else {
			out = out
				.set('hour', toNum(rule.hour, 0))
				.set('minute', toNum(rule.minute, 0))
				.set('second', toNum(rule.second, 0))
				.set('millisecond', 0);
		}
	} else {
		// START
		const today = now.startOf('day');

		// If today or past → start now
		if (mSelDay.isSameOrBefore(today, 'day')) {
			return now;
		}

		// If exactly the earliest allowed day → use rule time
		if (mSelDay.isSame(minAllowed, 'day')) {
			out = out
				.set('hour', toNum(rule.hour, 0))
				.set('minute', toNum(rule.minute, 0))
				.set('second', toNum(rule.second, 0))
				.set('millisecond', 0);
		} else {
			// Later days → start of day (00:01)
			out = out.set('hour', 0).set('minute', 1).set('second', 0).set('millisecond', 0);
		}
	}

	return out;
}

/**
 * Parse a server timestamp as UTC whether or not it carries a timezone designator.
 *
 * Server timestamps may arrive as '...T10:00:00.000Z' (Date → JSON) or as bare
 * '...T10:00:00'. dayjs.utc handles both (bare parses as UTC, a designator is
 * respected) — unlike the old `dayjs(value + 'Z')` pattern, which turned the
 * former into '...ZZ' → Invalid Date, so every comparison went false.
 */
export function parseServerDate(value) {
	return dayjs.utc(value);
}

export function adjustStartIfPast(startDate) {
	const start = dayjs(startDate);
	const now = dayjs();

	// If the start date is in the past, return now
	return start.isBefore(now) ? now : start;
}

const dateFunctions = {
	applyRuleTime,
	calculateDate,
	cleaningEndDateForMirvac,
	cleaningStartDateForMirvac,
	fourMonths,
	lastFridayOfTheMonthForMirvac,
	maxBookingDateForBHP,
	threeMonths,
	today,
	twelveMonthsFromToday,
	twoMonths,
	adjustStartIfPast,
	parseServerDate
};

export default dateFunctions;
